import { Capacitor } from '@capacitor/core'
import {
  AppUpdate,
  AppUpdateAvailability,
  AppUpdateResultCode,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update'
import { toast } from 'sonner'

export type PlayUpdateMode = 'flexible' | 'immediate'

let flexibleRestartToastShown = false
let flexibleListenerRegistered = false

export async function checkPlayUpdateAvailable(): Promise<PlayUpdateMode | null> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    return null
  }

  try {
    const info = await AppUpdate.getAppUpdateInfo()

    if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return null
    }

    if (info.flexibleUpdateAllowed) return 'flexible'
    if (info.immediateUpdateAllowed) return 'immediate'

    return null
  } catch (error) {
    console.warn('[in-app update] check:', error)
    return null
  }
}

async function ensureFlexibleUpdateListener(): Promise<void> {
  if (flexibleListenerRegistered) return

  flexibleListenerRegistered = true

  await AppUpdate.addListener('onFlexibleUpdateStateChange', (state) => {
    if (
      state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED &&
      !flexibleRestartToastShown
    ) {
      flexibleRestartToastShown = true
      toast.message('Atualização baixada', {
        description: 'Reinicie o app para aplicar a nova versão.',
        action: {
          label: 'Reiniciar',
          onClick: () => {
            void AppUpdate.completeFlexibleUpdate()
          },
        },
        duration: Infinity,
      })
    }
  })
}

export async function applyPlayUpdate(mode: PlayUpdateMode): Promise<void> {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'android') {
    return
  }

  try {
    if (mode === 'flexible') {
      await ensureFlexibleUpdateListener()

      const result = await AppUpdate.startFlexibleUpdate()
      if (result.code === AppUpdateResultCode.CANCELED) {
        await AppUpdate.removeAllListeners()
        flexibleListenerRegistered = false
        return
      }
      if (result.code !== AppUpdateResultCode.OK) {
        console.warn('[in-app update] flexible:', result.code)
        await AppUpdate.removeAllListeners()
        flexibleListenerRegistered = false
      }
      return
    }

    const result = await AppUpdate.performImmediateUpdate()
    if (
      result.code !== AppUpdateResultCode.OK &&
      result.code !== AppUpdateResultCode.CANCELED
    ) {
      console.warn('[in-app update] immediate:', result.code)
    }
  } catch (error) {
    console.warn('[in-app update] apply:', error)
  }
}
